import type { Entry, Layer } from "shared";
import { cn } from "@/lib/cn";

export type EntryListItem = {
	entry: Entry;
	layerTitle: string;
};

type Props = {
	items: EntryListItem[];
	selectedEntryId: string | null;
	onSelect: (entryId: string) => void;
	loading?: boolean;
};

function geomLabel(geomType: Entry["geomType"]): string {
	if (geomType === "point") return "Punkt";
	if (geomType === "line") return "Straße";
	return "Bereich";
}

export function buildEntryListItems(
	layers: Layer[],
	entriesByLayer: Record<string, Entry[]>,
): EntryListItem[] {
	const items: EntryListItem[] = [];
	for (const layer of layers) {
		const entries = entriesByLayer[layer.id] ?? [];
		for (const entry of entries) {
			items.push({ entry, layerTitle: layer.title });
		}
	}
	items.sort((a, b) => b.entry.updatedAt.localeCompare(a.entry.updatedAt));
	return items;
}

export function EntriesList({ items, selectedEntryId, onSelect, loading }: Props) {
	return (
		<div className="mt-1 flex min-h-0 flex-1 flex-col border-t border-border pt-2">
			<p className="mb-1 px-1.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">
				Einträge
				{items.length > 0 ? ` · ${items.length}` : ""}
			</p>
			{loading ? (
				<p className="px-1.5 text-xs text-fg-muted">Laden…</p>
			) : items.length === 0 ? (
				<p className="px-1.5 text-xs text-fg-muted">Noch keine Einträge auf dieser Karte.</p>
			) : (
				<ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
					{items.map(({ entry, layerTitle }) => {
						const selected = entry.id === selectedEntryId;
						return (
							<li key={entry.id}>
								<button
									type="button"
									onClick={() => onSelect(entry.id)}
									className={cn(
										"w-full rounded-[var(--radius-control)] px-2 py-1.5 text-left text-sm",
										selected
											? "bg-teal-500/10 font-medium text-fg"
											: "text-fg hover:bg-zinc-950/[0.04] dark:hover:bg-white/[0.06]",
									)}
								>
									<div className="truncate font-medium">{entry.title}</div>
									<div className="truncate text-xs text-fg-muted">
										{geomLabel(entry.geomType)} · {layerTitle}
									</div>
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}
